import React, { useEffect, useMemo, useRef, useState } from 'react';
import "./index.scss";

interface ToggleGroupProps {
  id?: string;
  options: Array<{
    id: string;
    label: string;
    value: string;
    disabled?: boolean;
  }>;
  value: string | string[];
  multiple?: boolean;
  disabled?: boolean;
  onChange: (value: string | string[]) => void;
}

const ToggleGroup = ({ id, options, value, disabled = false, multiple = false, onChange }: ToggleGroupProps) => {
  const [isToggle, setIsToggle] = useState<boolean>(false);
  const [isUnset, setIsUnset] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isUnsetTransitioning, setIsUnsetTransitioning] = useState<boolean>(false);

  const oldValue = useRef<string | string[] | undefined>(value);

  const selectedElement = useRef<HTMLButtonElement | null>(null);
  const oldSelectedElement = useRef<HTMLButtonElement | null>(null);

  const buttonRefs = useRef<HTMLButtonElement[]>([]);

  const resizeObserver = useRef<ResizeObserver | null>(null);

  const [selectedElementWidth, setSelectedElementWidth] = useState<number>(0);
  const [selectedElementLeft, setSelectedElementLeft] = useState<number>(0);

  useEffect(() => {
    if (oldValue.current === undefined || oldValue.current === null || oldValue.current === '') {
      setIsUnsetTransitioning(true);

      setTimeout(() => {
        setIsUnsetTransitioning(false);
      }, 200);
    }

    setIsUnset(value === undefined || value === null || value === '');

    oldValue.current = value;
  }, [value]);

  useEffect(() => {
    setIsToggle(options.length === 2 && !multiple && !isUnset);
  }, [options, multiple, isUnset]);

  useEffect(() => {
    if (!isUnset) {
      setSelectedIndex(options.findIndex((option) => option.value === value));
    } else {
      setSelectedIndex(null);
    }
  }, [options, value, isUnset]);

  useEffect(() => {
    oldSelectedElement.current = selectedElement.current;

    if (!isUnset && selectedIndex !== null) {
      selectedElement.current = buttonRefs.current[selectedIndex];
    } else {
      selectedElement.current = null;
    }

    if (selectedElement.current) {
      setSelectedElementWidth(selectedElement.current.offsetWidth);
      setSelectedElementLeft(selectedElement.current.offsetLeft);
    }
  }, [selectedIndex, isUnset]);

  useEffect(() => {
    resizeObserver.current = new ResizeObserver(entries => {
      for (let entry of entries) {
        const { width, left } = entry.contentRect;
        setSelectedElementWidth(width);
        setSelectedElementLeft(left);
      }
    });

    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (resizeObserver.current) {
      if (oldSelectedElement.current) resizeObserver.current.unobserve(oldSelectedElement.current);
      if (selectedElement.current) resizeObserver.current.observe(selectedElement.current);
    }
  }, [selectedElement]);

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only used when there are two options and is set to single (will toggle between the two options)

    if (!isToggle) return;

    const newSelectedIndex = selectedIndex === 0 ? 1 : 0;
    const newValue = options[newSelectedIndex].value;

    if (value !== newValue) onChange?.(newValue);
  };

  const handleButtonClick = (val: string) => {
    // Used when there are multiple options (will add or remove the option from the value)

    if (isToggle) return;

    if (multiple) {
      const newValue = [...value];

      if (!newValue.includes(val)) {
        newValue.push(val);
      } else {
        newValue.splice(newValue.indexOf(val), 1);
      }

      onChange?.(newValue);
    } else {
      if (value !== val) onChange?.(val);
    }
  };

  return (
    <div
      className={`toggle-group ${isToggle ? 'toggle-group--toggle' : ''} ${disabled ? 'toggle-group--disabled' : ''}`}
      onClick={handleContainerClick}
    >
      {!multiple &&
        <div className={`toggle-group__highlight ${isUnsetTransitioning ? 'toggle-group__highlight--unset-transitioning' : ''}`} style={{ width: `${selectedElementWidth}px`, left: `${selectedElementLeft}px`}} />
      }
      {options.map((option, index) => {
        const isChecked = Array.isArray(value) ? (value as any[]).includes(option.value) : value === option.value;

        return (
          <button
            ref={(el) => buttonRefs.current[index] = el as HTMLButtonElement}
            key={`${id || ''}-${option.id}`}
            role="radio" 
            type="button"
            aria-checked={isChecked} 
            className={`
              toggle-group__option
              ${isChecked ? 'toggle-group__option--active' : ''}
              ${option.disabled ? 'toggle-group__option--disabled' : ''}
              ${isToggle ? 'toggle-group__option--toggle' : ''}
              ${isUnset ? 'toggle-group__option--unset' : ''}
              ${multiple ? 'toggle-group__option--multiple' : ''}
              ${!multiple ? 'toggle-group__option--single' : ''}
              ${isUnsetTransitioning ? 'toggle-group__option--unset-transitioning' : ''}
            `}
            onClick={(e) => handleButtonClick(option.value)}
          >
            {option.label}
          </button>
        )
      }
    )}
    </div>
  );
};

export default ToggleGroup;